import {useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {BaSeButton, BaSeCheckbox} from "@base/react";
import {AppContext} from "../../context/app";
import ApiOptIn from "../../services/optIn";
import ApiShare from "../../services/share";
import Loading from "../../components/loading";
import CardInputCompany from "../../components/cards/card-input-company";
import ApiAuth from "../../services/auth";
import ToastContent from "../../components/toastContent";
import greenChecked from "../../assets/images/icons/green_checked.svg";
import "./index.scss";
import {eventTag, getCurrentCNPJ} from "../../services/utils";
import {toast} from "react-toastify";
import ApiGeneral from "../../services/general";
import {
  clearCompaniesCache,
  hasValidCompaniesCache,
  setCompaniesCache,
} from "../../services/companiesCache";

const event = {
  name: "company",
  category: "selecao_de_empresa",
  action: "click",
};

const SHARED_PLAN_BENEFITS = [
  "Acompanhar o planejamento em tempo real",
  "Registrar e visualizar comentários",
  "Visualizar orientações",
];

const CompaniesPage = ({ account }) => {
  const ApiOpt = ApiOptIn();
  const apiShare = ApiShare();
  const navigate = useNavigate();

  const location = window.location.host;
  const protocol = window.location.protocol;
  const { companies, toggleCompanies, setCompanies } = useContext(AppContext);
  const [optIn, setOptIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAgree, setSelectedAgree] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSharedCpfAccess, setShowSharedCpfAccess] = useState(false);
  const [requestingSharedPlan, setRequestingSharedPlan] = useState(false);

  const firstName = account?.name ? account.name.split(" ")[0] : "Usuario";
  const pendingSharedToken = sessionStorage.getItem("pendingSharedToken");
  const pendingSharedPath = sessionStorage.getItem("pendingSharedPath");
  const isSharedFlow = Boolean(pendingSharedToken);
  const isSharedPostCredit = pendingSharedPath?.includes(
    "/compartilhar/pos-credito/"
  );
  const currentCnpj = getCurrentCNPJ(companies) || null;
  const hasSelectedCompany = companies.some((company) => company.isActive);
  const canProceed = optIn || selectedAgree;

  const showSharedAccessScreen = useMemo(() => {
    if (!isSharedFlow) return false;
    if (companies.length > 0) return true;
    return showSharedCpfAccess;
  }, [isSharedFlow, companies.length, showSharedCpfAccess]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    // cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleRegisterCompany = () => {
    eventTag(
      "click-button",
      event?.category,
      event?.action,
      "Vincular minha empresa"
    );
    clearCompaniesCache()
    window.location.href = `${process.env.REACT_APP_CPE}/?voltar=${protocol}//${location}/`;
  };

  const clearSharedFlow = () => {
    sessionStorage.removeItem("pendingSharedToken");
    sessionStorage.removeItem("pendingSharedPath");
    sessionStorage.removeItem("processedSharedSelection");
  };

  const getOpt = () => {
    setLoading(true)
    ApiOpt.getOptIn()
      .then((response) => {
        const { data } = response
        setOptIn(data.opt_in)

        if (data.opt_in) {
          setSelectedAgree(true)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }
  const isCompleted = localStorage.getItem("hideGuidedTourDefault") === "true"
  const checkIsCompletedTutorial = () => {
    if (!isCompleted) {
      ApiAuth()
        .checkIsCompletedTutorial()
        .then((res) => {
          const { data } = res
          localStorage.setItem("hideGuidedTourDefault", data.steps)
          localStorage.setItem("hideGuidedTour", data.steps)
        })
        .catch((err) => {
          console.error("ERROR: ", err)
        })
    }
  }
  const postOpt = (page) => {
    ApiOpt.postOptIn()
      .catch((err) => console.warn(err))
      .finally(() => {
        eventTag(event?.name, event?.category, "submit", "Aceite de opt-in");
        setOptIn(true);
        navigate(page);
      });
  };
  const handleCheckboxAgree = (checked) => {
    setSelectedAgree(checked);
    eventTag(event?.name, event?.category, event?.action, "Aceite de opt-in");
  };
  const goToHome = () => {
    if (optIn === false) {
      postOpt("/inicio");
    } else {
      navigate("/inicio");
    }
  }

  const handleContinueWithCpf = () => {
    eventTag(
      "click-button",
      event?.category,
      event?.action,
      "Continuar com o meu CPF"
    )

    ApiGeneral().sendDigitalMoments('logged_cpf')

    if (isSharedFlow) {
      setShowSharedCpfAccess(true);
      return;
    }

    goToHome();
  };

  const handleRequestSharedPlan = async () => {
    if (!pendingSharedToken || !canProceed) return;
    if (companies.length > 0 && !hasSelectedCompany) return;

    setRequestingSharedPlan(true);

    const request = isSharedPostCredit
      ? apiShare.acceptSharedPlanningPostCredit(
          pendingSharedToken,
          currentCnpj
        )
      : apiShare.acceptSharedPlanning(pendingSharedToken, currentCnpj);

    try {
      const { data } = await request;
      const sharedTitle = data.plan_title || data.post_credit_title || "";

      clearSharedFlow();
      navigate("/inicio", {
        state: {
          sharedPlanAccepted: true,
          sharedPlanTitle: sharedTitle,
        },
      });
    } catch (error) {
      const {status, details} = error;
      const message = error?.details && details?.detail ? details?.detail : "Não foi possivel solicitar acesso no momento."
      console.error(status)
      console.error(details)

      if (status === 412) {
        toast.warning(
          <ToastContent
            description={message}
            title="Ops!"
          />,
          {
            autoClose: 5000,
            pauseOnFocusLoss: false,
          }
        )
      } else {
        toast.error(
          <ToastContent
            description={message}
            title="Erro ao solicitar acesso!"
          />
        )
      }
      clearSharedFlow()
      navigate("/inicio")
    } finally {
      setRequestingSharedPlan(false);
    }
  };
  const getCompanies = () => {
    setLoading(true)

    ApiAuth().getCompanies()
      .then((res) => {
        const { data } = res

        const newData =
          data.company_list.length > 0
            ? data.company_list.map((item) => ({
                ...item,
                isActive: !!item.getCurrentCNPJ,
              }))
            : []
        setCompaniesCache(newData)
        setCompanies(newData)
      })
      .catch((err) => {
        console.warn(err)
        clearCompaniesCache()
        setCompanies([])
        setLoading(false)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getOpt()
    checkIsCompletedTutorial()

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!hasValidCompaniesCache()) {
      getCompanies()
    }

    // eslint-disable-next-line
  }, [])

  if (loading) {
    return <Loading fullPage />;
  }

  const renderSharedAccessContent = () => (
    <div className="shared-access">
      <h2 className="salutation">Olá, {firstName}</h2>
      <h3 className="shared-access__title">Bem-vindo ao Acompanhamento.</h3>
      <p className="shared-access__description">
        Você foi convidado para acompanhar um planejamento financeiro.
      </p>
      <p className="shared-access__description">
        Selecione a empresa que você deseja solicitar o acesso.
      </p>

      <div className="shared-access__layout">
        {companies.length ? (
          <div className="shared-access__companies">
            {companies.map((company, index) => (
              <CardInputCompany
                key={company.cnpj || index}
                data={company}
                callBack={(cnpj) => toggleCompanies(cnpj)}
                selected={company.isActive}
                event={event}
              />
            ))}
          </div>
        ) : null}

        <div className="shared-access__card">
          <h4>O que você poderá fazer</h4>
          <div className="shared-access__divider" />
          <ul>
            {SHARED_PLAN_BENEFITS.map((item) => (
              <li key={item}>
                <img src={greenChecked} alt="" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="shared-access__actions">
        <BaSeButton
          color="#2E5DE6"
          isBold={true}
          buttonType="button"
          value="Solicitar acesso"
          isDisabled={
            requestingSharedPlan ||
            !canProceed ||
            (companies.length > 0 && !hasSelectedCompany)
          }
          onClick={handleRequestSharedPlan}
        />
      </div>
    </div>
  );

  return (
    <div className={`related-companies-container ${showSharedAccessScreen ? "shared-flow" : ""}`}>
      <div className="container">
        {showSharedAccessScreen ? (
          renderSharedAccessContent()
        ) : (
          <>
            <h2 className="salutation">Olá, {firstName}!</h2>
            <p
              className={`description-companies ${
                !isMobile ? "color-desktop" : ""
              }`}
            >
              {companies.length ? (
                <>
                  Essas são as empresas vinculadas ao seu CPF. Selecione uma para
                  começar a utilizar a{" "}
                  {isMobile ? (
                    <strong>Planejadora Sebrae</strong>
                  ) : (
                    "Planejadora Sebrae"
                  )}
                  :
                </>
              ) : isSharedFlow ? (
                <>
                  Parece que ainda não ha nenhuma empresa vinculada ao seu CPF
                  nos sistemas do Sebrae. Que tal vincular a sua agora?
                </>
              ) : (
                <>
                  Não identificamos empresas vinculadas ao seu CPF. Cadastre a
                  sua empresa ou continue com o seu CPF.
                </>
              )}
            </p>

            {companies.length ? (
              <div className="available-companies">
                {companies.map((company, index) => (
                  <CardInputCompany
                    key={company.cnpj || index}
                    data={company}
                    callBack={(cnpj) => toggleCompanies(cnpj)}
                    selected={company.isActive}
                    event={event}
                  />
                ))}
              </div>
            ) : (
              <div className="company__register__area">
                <BaSeButton
                  color="#2E5DE6"
                  isBold={true}
                  buttonType="button"
                  value="Vincular minha empresa"
                  onClick={handleRegisterCompany}
                />
                <div className="text-or">
                  <span>ou</span>
                </div>
                <button
                  type="button"
                  className="btn-white-outline"
                  disabled={!selectedAgree}
                  onClick={handleContinueWithCpf}
                >
                  Continuar com o meu CPF
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <div className="company__bottom__content">
        <div className="container">
          <div className="read-terms">
            {!optIn && (
              <div className="read-terms">
                <BaSeCheckbox
                  label={
                    <span>
                      Declaro que li e concordo com os{" "}
                      <a
                        href={
                          process.env.REACT_APP_API +
                          "/media/termo_de_uso_planejadora%20_sebrae.pdf"
                        }
                        className="underlined-term"
                        target="_blank"
                        rel="noreferrer"
                        onClick={() =>
                          eventTag(
                            "click-button",
                            event?.category,
                            event?.action,
                            "Termos de Uso"
                          )
                        }
                      >
                        Termos de Uso.
                      </a>
                    </span>
                  }
                  id="company_read_and_agree"
                  name="read_and_agree"
                  checked={selectedAgree}
                  onChange={handleCheckboxAgree}
                  color={"#0f438a"}
                />
              </div>
            )}
          </div>
          {!showSharedAccessScreen && companies.length ? (
            <div className="company__button">
              <div className="company__button-continue">
                <BaSeButton
                  color={"#2E5DE6"}
                  isBold={true}
                  isDisabled={!hasSelectedCompany || !canProceed}
                  onClick={() => {
                    ApiGeneral().sendDigitalMoments('logged_cnpj', { cnpj: currentCnpj })
                    goToHome()
                    eventTag(
                      "click-button",
                      event?.category,
                      event?.action,
                      "Continuar"
                    )
                  }}
                  value="Continuar"
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CompaniesPage;
